import { baseStyleSheet } from '@Styles/styles';
import * as Api from '@ViewModels';
import { RadioButton } from '@WebComponents/RadioButton';
import { TextInput } from '@WebComponents/TextInput';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast } from 'sonner';
import { copyToClipboard, getLogoUrlForIntegrationProvider } from '../../../../models/UiUtils';
import Ams360IconUrl from '../../../../web/assets/ams360.png';
import HawksoftIconUrl from '../../../../web/assets/hawksoft_logo.png';
import HubSpotLogo from '../../../../web/assets/hubspot_logo.jpg';
import EzLynxLogo from '../../../../web/assets/logo_ezlynx.png';
import NasaSoftLogo from '../../../../web/assets/logo_nasasoft.png';
import RedtailLogo from '../../../../web/assets/logo_redtail.png';
import WealthboxLogo from '../../../../web/assets/logo_wealthbox.png';
import XanatekLogo from '../../../../web/assets/logo_xanatek.jpg';
import { useConfigurableIntegrationSettingsContext } from './context';
import { styleSheet } from './styles';

export const getDefaultOwnershipRoleForIntegrationType = (integrationType: Api.ConfigurableIntegrationType) => {
	switch (integrationType) {
		case Api.ConfigurableIntegrationType.Clio:
			return Api.ClioIntegrationOwnershipRole.ResponsibleAttorney;
		case Api.ConfigurableIntegrationType.Ams360:
			return Api.Ams360IntegrationOwnershipRole.Executive;
		case Api.ConfigurableIntegrationType.Epic:
			return Api.BaseInsuranceIntegrationOwnershipRole.Producer;
		case Api.ConfigurableIntegrationType.HawkSoft:
			return Api.BaseInsuranceIntegrationOwnershipRole.Producer;
		case Api.ConfigurableIntegrationType.Eclipse:
			return Api.BaseInsuranceIntegrationOwnershipRole.Producer;
		case Api.ConfigurableIntegrationType.QQ:
			return Api.BaseInsuranceIntegrationOwnershipRole.Producer;
		case Api.ConfigurableIntegrationType.Wealthbox:
			return Api.WealthboxIntegrationOwnershipRole.AssignedTo;
		default:
			return null;
	}
};

const getIconUrl = (integrationType: Api.ConfigurableIntegrationType) => {
	switch (integrationType) {
		case Api.ConfigurableIntegrationType.Ams360:
			return Ams360IconUrl;
		case Api.ConfigurableIntegrationType.HawkSoft:
			return HawksoftIconUrl;
		case Api.ConfigurableIntegrationType.Redtail:
			return RedtailLogo;
		case Api.ConfigurableIntegrationType.Eclipse:
			return NasaSoftLogo;
		case Api.ConfigurableIntegrationType.EzLynx:
			return EzLynxLogo;
		case Api.ConfigurableIntegrationType.Xanatek:
			return XanatekLogo;
		case Api.ConfigurableIntegrationType.HubSpot:
			return HubSpotLogo;
		case Api.ConfigurableIntegrationType.QuickBooksOnline:
		case Api.ConfigurableIntegrationType.Xero:
		case Api.ConfigurableIntegrationType.Salesforce:
			return getLogoUrlForIntegrationProvider(integrationType);
		case Api.ConfigurableIntegrationType.Wealthbox:
			return WealthboxLogo;
		default:
			return null;
	}
};

const getDescription = (integrationType: Api.ConfigurableIntegrationType, integrationName: string) => {
	switch (integrationType) {
		case Api.ConfigurableIntegrationType.EzLynx:
			return `Levitate can integrate with ${integrationName} to pull in client information, including names, emails, phone numbers, type, line of business, and renewal dates. Customers must send their data to the report email address.`;
		case Api.ConfigurableIntegrationType.QQ:
			return `Levitate integrates with Vertafore QQCatalyst to pull in your contact data including names, email addresses, birthdays, physical addresses, policies, renewal dates, and more.`;
		default:
			return `Levitate can integrate with ${integrationName} to automatically pull in client information, including names, emails, phone numbers, type, line of business, and renewal dates. ${
				integrationType === Api.ConfigurableIntegrationType.Eclipse
					? `${integrationName} will also send updates to Levitate on a 4-hour interval.`
					: `We will also sync with ${integrationName} nightly to keep the data synced.`
			}${
				integrationType === Api.ConfigurableIntegrationType.Clio
					? ` Note: Syncs manually kicked off during Clio "Peak Hours" (04:00-19:00 Pacific Time, Monday-Friday) will be extremely slow due to Clio's API throttling.`
					: ''
			}`;
	}
};

export const AdminConfigurableIntegrationSettingsIcon: React.FC<{
	styles?: StyleDeclarationValue[];
}> = ({ styles = [] }) => {
	const { integrationType } = useConfigurableIntegrationSettingsContext();
	return (
		<img
			className={css(
				styleSheet.icon,
				integrationType === Api.ConfigurableIntegrationType.Eclipse && styleSheet.nasasoftLogo,
				...styles
			)}
			src={getIconUrl(integrationType)}
		/>
	);
};

export const AdminConfigurableIntegrationSettingsHeader: React.FC = observer(() => {
	const { integrationType, integrationName } = useConfigurableIntegrationSettingsContext();

	return (
		<section>
			<AdminConfigurableIntegrationSettingsIcon />
			<div className={css(styleSheet.instructionsText)}>{getDescription(integrationType, integrationName)}</div>
		</section>
	);
});

export const AdminConfigurableIntegrationSettingsSetupSteps: React.FC = () => {
	const { integrationType, integration, onOwnerShipRoleChanged, ownershipRole, accountId } =
		useConfigurableIntegrationSettingsContext();

	const copyLink = React.useCallback(
		(value: string) => () => {
			const success = copyToClipboard(value);

			if (success) {
				toast.success('Copied value to clipboard!');
			} else {
				toast.error('Unable to copy to clipboard...');
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const renderCopyButton = React.useCallback(
		(valueToCopy: string) => {
			return (
				<button className={css(baseStyleSheet.brandLink, styleSheet.copyButton)} onClick={copyLink(valueToCopy)}>
					Copy to Clipboard
				</button>
			);
		},
		[copyLink]
	);

	const changeOwnershipRole = React.useCallback(
		(option: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
			let role: string;
			switch (integrationType) {
				case Api.ConfigurableIntegrationType.Ams360:
					role =
						option === Api.Ams360IntegrationOwnershipRole.Executive && !!e.target.checked
							? Api.Ams360IntegrationOwnershipRole.Executive
							: Api.Ams360IntegrationOwnershipRole.Representative;
					break;
				case Api.ConfigurableIntegrationType.HawkSoft:
					role =
						option === Api.BaseInsuranceIntegrationOwnershipRole.Producer && !!e.target.checked
							? Api.BaseInsuranceIntegrationOwnershipRole.Producer
							: Api.BaseInsuranceIntegrationOwnershipRole.CSR;
					break;
				case Api.ConfigurableIntegrationType.Eclipse:
					role =
						option === Api.BaseInsuranceIntegrationOwnershipRole.Producer && !!e.target.checked
							? Api.BaseInsuranceIntegrationOwnershipRole.Producer
							: Api.BaseInsuranceIntegrationOwnershipRole.CSR;
					break;
				default:
					break;
			}

			if (role) {
				onOwnerShipRoleChanged?.(role);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[onOwnerShipRoleChanged]
	);

	switch (integrationType) {
		case Api.ConfigurableIntegrationType.Ams360: {
			const { credentials } = integration as Api.IAms360Integration;
			return (
				<div>
					<div className={css(styleSheet.stepText)}>
						<div>Step 1:</div>
						<div>Please generate a Levitate login and password below, specifically for the AMS360 integration.</div>
					</div>
					<div className={css(styleSheet.labelText)}>Login ID:</div>
					<div className={css(styleSheet.textInputContainer)}>
						<TextInput
							className={css(styleSheet.textInput)}
							disabled={true}
							inputId='ams360-login-id'
							type='text'
							value={credentials.userName}
						/>

						{renderCopyButton(credentials.userName)}
					</div>
					<div className={css(styleSheet.labelText)}>Password:</div>
					<div className={css(styleSheet.textInputContainer)}>
						<TextInput
							className={css(styleSheet.textInput)}
							disabled={true}
							inputId='ams360-password'
							type='text'
							value={credentials.password}
						/>

						{renderCopyButton(credentials.password)}
					</div>
					<div className={css(styleSheet.stepText)}>
						<div>Step 2:</div>
						<div>Setup AMS360 Notification Service to notify Levitate of updates in real-time:</div>
					</div>
					<div className={css(styleSheet.labelText)}>Destination Address:</div>
					<div className={css(styleSheet.textInputContainer)}>
						<TextInput
							className={css(styleSheet.textInput)}
							disabled={true}
							inputId='ams360-destination'
							type='text'
							value='https://webhooks.levitate.ai/webhooks/integrations/ams360'
						/>
						{renderCopyButton('https://webhooks.levitate.ai/webhooks/integrations/ams360')}
					</div>
					<div className={css(styleSheet.labelText)}>Authentication Code:</div>
					<div className={css(styleSheet.textInputContainer)}>
						<TextInput
							className={css(styleSheet.textInput)}
							disabled={true}
							inputId='ams360-auth-code'
							type='text'
							value={accountId}
						/>
						{renderCopyButton(accountId)}
					</div>
					<div className={css(styleSheet.labelText)}>Notification Version:</div>
					<div className={css(styleSheet.textInputContainer)}>
						<TextInput
							className={css(styleSheet.textInput)}
							disabled={true}
							inputId='ams360-notification-version'
							type='text'
							value='2.0'
						/>
						{renderCopyButton('2.0')}
					</div>
					<div className={css(styleSheet.stepText)}>
						<div>Step 3:</div>
						<div>Choose how you&apos;d like to map the data</div>
					</div>
					<div className={css(styleSheet.stepText)}>
						<RadioButton
							checked={ownershipRole === Api.Ams360IntegrationOwnershipRole.Executive}
							id='Ams360Ownership-Executive'
							name='Ams360Ownership-Executive'
							onChange={changeOwnershipRole(Api.Ams360IntegrationOwnershipRole.Executive)}
						>
							<span>Map ownership according to Executive</span>
						</RadioButton>
						<RadioButton
							checked={ownershipRole === Api.Ams360IntegrationOwnershipRole.Representative}
							id='Ams360Ownership-Representative'
							name='Ams360Ownership-Representative'
							onChange={changeOwnershipRole(Api.Ams360IntegrationOwnershipRole.Representative)}
						>
							<span>Map ownership according to Representative</span>
						</RadioButton>
					</div>
					<div className={css(styleSheet.stepText)}>
						<div>Step 4:</div>
						<div>Please enter the agency’s AMS360 ID below:</div>
					</div>
				</div>
			);
		}
		case Api.ConfigurableIntegrationType.HawkSoft: {
			return (
				<div>
					<div className={css(styleSheet.stepText)}>
						<div>Step 1:</div>
						<div>
							{"Please go to HawkSoft's market place to enable Levitate. "}
							<a
								href='https://help.levitate.ai/en/articles/3859748-hawksoft-integration-guide'
								target='_blank'
								rel='noreferrer'
							>
								See integration guide.
							</a>
						</div>
					</div>
					<div className={css(styleSheet.stepText)}>
						<div>Step 2:</div>
						<div>Choose how you&apos;d like to map the data</div>
					</div>
					<div className={css(styleSheet.stepText)}>
						<RadioButton
							checked={ownershipRole === Api.BaseInsuranceIntegrationOwnershipRole.Producer}
							id='HawksoftOwnership-Producers'
							name='HawksoftOwnership-Producers'
							onChange={changeOwnershipRole(Api.BaseInsuranceIntegrationOwnershipRole.Producer)}
						>
							<span>Map ownership according to producers</span>
						</RadioButton>
						<RadioButton
							checked={ownershipRole === Api.BaseInsuranceIntegrationOwnershipRole.CSR}
							id='HawksoftOwnership-CSRs'
							name='HawksoftOwnership-CSRs'
							onChange={changeOwnershipRole(Api.BaseInsuranceIntegrationOwnershipRole.CSR)}
						>
							<span>Map ownership according to CSRs</span>
						</RadioButton>
					</div>
					<div className={css(styleSheet.stepText)}>
						<div>Step 3:</div>
						<div>Enter your HawkSoft Agency ID below:</div>
					</div>
				</div>
			);
		}
		case Api.ConfigurableIntegrationType.Eclipse: {
			return (
				<div>
					<div className={css(styleSheet.stepText)}>
						<div>Step 1:</div>
						<div>Choose how you&apos;d like to map the data</div>
					</div>
					<div className={css(styleSheet.stepText)}>
						<RadioButton
							checked={ownershipRole === Api.BaseInsuranceIntegrationOwnershipRole.Producer}
							id='EclipseOwnership-Producers'
							name='EclipseOwnership-Producers'
							onChange={changeOwnershipRole(Api.BaseInsuranceIntegrationOwnershipRole.Producer)}
						>
							<span>Map ownership according to producers</span>
						</RadioButton>
						<RadioButton
							checked={ownershipRole === Api.BaseInsuranceIntegrationOwnershipRole.CSR}
							id='EclipseOwnership-CSRs'
							name='EclipseOwnership-CSRs'
							onChange={changeOwnershipRole(Api.BaseInsuranceIntegrationOwnershipRole.CSR)}
						>
							<span>Map ownership according to CSRs</span>
						</RadioButton>
					</div>
					<div className={css(styleSheet.stepText)}>
						<div>Step 2:</div>
						<div>Enter your Eclipse Agency ID below:</div>
					</div>
				</div>
			);
		}
		default: {
			return null;
		}
	}
};
